// Staff (SM-712 / SM-682; sub-broker view SM-852 / SM-858). Everyone with
// VIEW reads the node's staff; invite / suspend / resume / remove /
// re-role are offered only with STAFF_MANAGE, which since the PO decision
// of 2026-10-08 reaches the member's node AND its descendants
// (lib/capabilities.ts): a parent's OWNER or ADMINISTRATOR staffs its
// sub-brokers, under the same grant matrix (lib/staffAccess.ts). Every
// action is a confirm with a mandatory reason and shows the audit
// reference on success; the last-owner and escalation rules are the
// server's, and its refusal is shown through the error catalog. "Create
// sub-broker" (CHILD_CREATE) lands a DRAFT awaiting exchange approval.
//
// "Include sub-brokers" (?staffScope=subtree) lists the node's and every
// descendant's staff with a Node column; clicking a user id
// (?staffUser=) shows the nodes that person reaches. Row actions call the
// ROW's node. A membership an ancestor placed carries "Assigned by parent
// broker" — the ancestor is never named.
//
//   GET  /api/broker/{id}/staff?scope=direct|subtree&user_id=&include_removed=&offset=&limit=
//   POST /api/broker/{id}/staff                              InviteStaffRequest (one per node)
//   POST /api/broker/{id}/staff/{mid}/suspend|resume|remove  StaffTransitionRequest
//   POST /api/broker/{id}/staff/{mid}/role                   ChangeStaffRoleRequest
//   POST /api/broker/{id}/staff/{mid}/resend-invite          StaffTransitionRequest (SM-968)
//   POST /api/broker/{id}/children                           CreateBrokerRequest
//
// SM-968: people are invited by email and shown by name and email; the
// staff id sits under "Technical details". An invitee who has not set a
// password yet reads "Pending sign-up" and can be sent a new link.
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { capabilityGuard, Guarded } from "@/components/layout/guard";
import { PageHeader } from "@/components/layout/PageHeader";
import { useCan, useScope } from "@/components/layout/ScopeContext";
import { SectionCard } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { ByActor, Id, Pager, TableFrame, Time } from "@/components/ui/Common";
import { EmptyState, QueryBlock } from "@/components/ui/States";
import { Select, Segmented } from "@/components/ui/Field";
import { ConfirmReasonDialog } from "@/components/ui/ConfirmReasonDialog";
import { CreateChildDialog } from "@/components/CreateChildDialog";
import { InviteStaffDialog } from "@/components/InviteStaffDialog";
import { changeStaffRole, fetchDescendants, fetchStaff, keys, resendInvite, staffCommand, type StaffCommand } from "@/lib/queries";
import { membershipStatusTone } from "@/lib/status";
import {
  canActOnRow,
  grantableRoles,
  isPendingSignUp,
  manageRoleOn,
  memberLabel,
  parseStaffSearch,
  staffListParams,
  type StaffSearch,
} from "@/lib/staffAccess";
import type { StaffMembershipItem, StaffRole } from "@/lib/types";
import { useI18n } from "@/i18n/I18nProvider";

export const Route = createFileRoute("/_app/staff")({
  validateSearch: (s: Record<string, unknown>): StaffSearch => parseStaffSearch(s),
  beforeLoad: capabilityGuard("VIEW"),
  component: StaffPage,
});

const LIMIT = 25;

type Action =
  | { kind: "invite"; email?: string; role?: StaffRole }
  | { kind: "resend"; m: StaffMembershipItem }
  | { kind: "child" }
  | { kind: StaffCommand; m: StaffMembershipItem }
  | { kind: "role"; m: StaffMembershipItem };

function StaffPage() {
  const { t } = useI18n();
  const { guard } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const scope = useScope();
  const canManage = useCan("STAFF_MANAGE");
  const canCreateChild = useCan("CHILD_CREATE");
  const qc = useQueryClient();
  const [includeRemoved, setIncludeRemoved] = useState(false);
  // the page offset belongs to one node (SM-957): another node's list
  // starts at its first page, as the scope, user and removed filters do
  const [paging, setPaging] = useState({ nodeId: scope.nodeId, offset: 0 });
  const offset = paging.nodeId === scope.nodeId ? paging.offset : 0;
  const setOffset = (o: number) => setPaging({ nodeId: scope.nodeId, offset: o });
  const [action, setAction] = useState<Action | null>(null);

  const subtree = search.staffScope === "subtree";
  const pathIds = scope.path.path.map((p) => p.broker_id);
  const callerRole = manageRoleOn(scope.staff, pathIds.length > 0 ? pathIds : [scope.nodeId]);
  const nodeDepth = scope.node?.broker_depth ?? 0;

  // Whether the node has sub-brokers: the toggle and the Nodes field show only then.
  const kids = useQuery({
    queryKey: keys.descendants(scope.nodeId, { limit: 1 }),
    queryFn: () => fetchDescendants(scope.nodeId, { limit: 1 }),
    enabled: guard.kind === "allow",
    staleTime: 60_000,
  });
  const hasDescendants = (kids.data?.page.total ?? 0) > 0;

  const params = staffListParams(search, { includeRemoved, offset, limit: LIMIT });
  const probe = { queryKey: keys.staff(scope.nodeId, params), queryFn: () => fetchStaff(scope.nodeId, params) };
  const q = useQuery({ ...probe, enabled: guard.kind === "allow" });
  const refresh = (nodeIds: string[] = []) => {
    for (const id of new Set([scope.nodeId, ...nodeIds])) void qc.invalidateQueries({ queryKey: ["broker", id, "staff"] });
  };
  const setSearch = (patch: Partial<StaffSearch>) => {
    setOffset(0);
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  };
  const rowsActionable = (m: StaffMembershipItem) => canManage && canActOnRow(scope.staff, pathIds, m);

  return (
    <Guarded guard={guard} probe={probe}>
      <PageHeader
        title={t("staff.title")}
        subtitle={t("staff.subtitle")}
        coverage={subtree ? "subtree" : "node"}
        coverageLabel={subtree ? t("staff.coverageSubtree") : undefined}
        actions={
          <>
            {canCreateChild && (
              <Button variant="ghost" onClick={() => setAction({ kind: "child" })}>
                {t("child.open")}
              </Button>
            )}
            {canManage && <Button onClick={() => setAction({ kind: "invite" })}>{t("staff.invite")}</Button>}
          </>
        }
      />
      {!canManage && <p className="banner mb-4 text-xs">{t("staff.readOnly")}</p>}
      {search.staffUser && (
        <div className="banner mb-4 items-center text-xs" role="status">
          <span className="min-w-0 flex-1">
            {t("staff.accessFor")}{" "}
            {q.data?.items?.[0] ? (
              <span className="font-semibold text-t-1">{memberLabel(q.data.items[0])}</span>
            ) : (
              <Id value={search.staffUser} className="text-t-1" />
            )}
          </span>
          {canManage && (
            <Button
              size="sm"
              onClick={() => setAction({ kind: "invite", email: q.data?.items?.[0]?.email, role: q.data?.items?.[0]?.role })}
            >
              {t("staff.addNodeAccess")}
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setSearch({ staffUser: undefined })} aria-label={t("staff.clearFilter")}>
            {t("staff.clearFilter")} ✕
          </Button>
        </div>
      )}
      <SectionCard
        title={t("staff.members")}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            {hasDescendants && (
              <Segmented
                label={t("staff.coverage")}
                value={subtree ? "subtree" : "node"}
                options={[
                  { value: "node", label: t("staff.thisNode") },
                  { value: "subtree", label: t("staff.includeSubBrokers") },
                ]}
                onChange={(v) => setSearch({ staffScope: v === "subtree" ? "subtree" : undefined })}
                className="[&>span]:sr-only"
              />
            )}
            <label className="flex items-center gap-2 text-xs text-t-2">
              <input
                type="checkbox"
                checked={includeRemoved}
                onChange={(e) => {
                  setIncludeRemoved(e.target.checked);
                  setOffset(0);
                }}
              />
              {t("staff.includeRemoved")}
            </label>
          </div>
        }
      >
        <QueryBlock
          query={q}
          empty={(d) =>
            (d.items ?? []).length === 0 && !d.page?.total ? (
              <EmptyState title={subtree ? t("staff.emptySubtree") : t("staff.empty")} />
            ) : null
          }
        >
          {(d) => (
            <>
              {/* a page past the end (members gone since it was opened) keeps the pager to step back */}
              {(d.items ?? []).length === 0 ? (
                <EmptyState title={subtree ? t("staff.emptySubtree") : t("staff.empty")} />
              ) : (
              <TableFrame label={t("staff.members")}>
                <table className="tbl">
                  <thead>
                    <tr>
                      {subtree && <th scope="col">{t("staff.node")}</th>}
                      <th scope="col">{t("staff.user")}</th>
                      <th scope="col">{t("staff.role")}</th>
                      <th scope="col">{t("common.status")}</th>
                      <th scope="col" className="md:hidden">
                        {t("staff.dates")}
                      </th>
                      <th scope="col" className="hidden md:table-cell">
                        {t("staff.invited")}
                      </th>
                      <th scope="col" className="hidden md:table-cell">
                        {t("staff.activated")}
                      </th>
                      {canManage && <th scope="col">{t("common.actions")}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(d.items ?? []).map((m) => (
                      <tr key={m.membership_id}>
                        {subtree && (
                          <td>
                            <button
                              type="button"
                              className="whitespace-nowrap text-left text-sm font-semibold text-t-1 hover:underline"
                              onClick={() => void navigate({ search: (prev) => ({ ...prev, node: m.broker_id }) })}
                              title={t("staff.openNode")}
                            >
                              {m.broker_display_name || m.broker_id}
                            </button>
                            <Id value={m.broker_id} className="block text-2xs text-t-3" />
                          </td>
                        )}
                        <td>
                          {subtree ? (
                            <button
                              type="button"
                              className="text-left hover:underline"
                              onClick={() => setSearch({ staffUser: m.user_id })}
                              title={t("staff.filterUser")}
                            >
                              <MemberName m={m} />
                            </button>
                          ) : (
                            <MemberName m={m} />
                          )}
                          <div className="mt-1 flex flex-wrap gap-1">
                            {m.user_id === scope.staff.user_id && <Badge tone="accent">{t("staff.you")}</Badge>}
                            {isPendingSignUp(m) && (
                              <Badge tone="warn" title={t("staff.pendingSignUpHint")}>
                                {t("staff.pendingSignUp")}
                              </Badge>
                            )}
                          </div>
                          <details className="mt-1 text-2xs text-t-3">
                            <summary className="cursor-pointer select-none">{t("staff.technicalDetails")}</summary>
                            <span className="mt-1 block">
                              {t("staff.staffId")} <Id value={m.staff_id || m.user_id} />
                            </span>
                          </details>
                        </td>
                        <td>
                          <span className="whitespace-nowrap">{t(`role.${m.role}`)}</span>
                          {m.granted_by_ancestor && (
                            <Badge tone="neutral" className="mt-1 flex w-fit whitespace-nowrap" title={t("staff.assignedByParentHint")}>
                              {t("staff.assignedByParent")}
                            </Badge>
                          )}
                        </td>
                        <td>
                          <Badge tone={membershipStatusTone(m.status)}>{t(`membershipStatus.${m.status}`)}</Badge>
                        </td>
                        <td className="text-xs md:hidden">
                          <Time ms={m.invited_at_unix_ms} />
                          <span className="block text-2xs text-t-3">
                            {m.activated_at_unix_ms ? (
                              <>
                                {t("staff.activated")}: <Time ms={m.activated_at_unix_ms} />
                              </>
                            ) : (
                              <ByActor id={m.invited_by} kind={m.invited_by_kind} name={m.invited_by_name} />
                            )}
                          </span>
                        </td>
                        <td className="hidden text-xs md:table-cell">
                          <Time ms={m.invited_at_unix_ms} />
                          <span className="block text-2xs text-t-3">
                            <ByActor id={m.invited_by} kind={m.invited_by_kind} name={m.invited_by_name} />
                          </span>
                        </td>
                        <td className="hidden text-xs md:table-cell">
                          <Time ms={m.activated_at_unix_ms} />
                        </td>
                        {canManage && (
                          <td>
                            {rowsActionable(m) ? (
                              <RowActions m={m} onAction={setAction} />
                            ) : (
                              <span className="text-2xs text-t-4">—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableFrame>
              )}
              <Pager page={d.page} onOffset={setOffset} />
            </>
          )}
        </QueryBlock>
      </SectionCard>

      {action?.kind === "invite" && (
        <InviteStaffDialog
          nodeId={scope.nodeId}
          nodeName={scope.node?.display_name ?? scope.nodeId}
          nodeDepth={nodeDepth}
          hasDescendants={hasDescendants}
          callerRole={callerRole}
          sessionEmail={scope.me.email}
          initialEmail={action.email}
          initialRole={action.role}
          onClose={() => setAction(null)}
          onTouched={refresh}
        />
      )}
      {action?.kind === "child" && (
        <CreateChildDialog nodeId={scope.nodeId} parentName={scope.node?.display_name ?? scope.nodeId} onClose={() => setAction(null)} />
      )}
      {action?.kind === "role" && (
        <RoleDialog
          m={action.m}
          roles={grantableRoles(manageRoleOn(scope.staff, [...pathIds, action.m.broker_id]))}
          onClose={() => setAction(null)}
          onDone={() => refresh([action.m.broker_id])}
        />
      )}
      {action?.kind === "resend" && (
        <ConfirmReasonDialog
          title={t("staff.resend.title", { user: memberLabel(action.m) })}
          description={t("staff.resend.body", { email: action.m.email ?? "" })}
          confirmLabel={t("staff.resend.confirm")}
          onConfirm={async (reason) => {
            const res = await resendInvite(action.m.broker_id, action.m.membership_id, { reason });
            return { auditIds: res.auditIds };
          }}
          onClose={() => setAction(null)}
          onDone={() => {
            setAction(null);
            refresh([action.m.broker_id]);
          }}
        />
      )}
      {(action?.kind === "suspend" || action?.kind === "resume" || action?.kind === "remove") && (
        <ConfirmReasonDialog
          title={t(`staff.cmd.${action.kind}.title`, { user: memberLabel(action.m) })}
          description={t(`staff.cmd.${action.kind}.body`)}
          confirmLabel={t(`staff.cmd.${action.kind}.confirm`)}
          danger={action.kind !== "resume"}
          onConfirm={async (reason) => {
            // the ROW's node: a descendant's member is managed on its own node
            const res = await staffCommand(action.m.broker_id, action.m.membership_id, action.kind as StaffCommand, { reason });
            return { auditIds: res.auditIds };
          }}
          onClose={() => setAction(null)}
          onDone={() => {
            setAction(null);
            refresh([action.m.broker_id]);
          }}
        />
      )}
    </Guarded>
  );
}

function RowActions({ m, onAction }: { m: StaffMembershipItem; onAction: (a: Action) => void }) {
  const { t } = useI18n();
  if (m.status === "REMOVED") return <span className="text-2xs text-t-4">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {isPendingSignUp(m) && (
        <Button size="sm" variant="ghost" onClick={() => onAction({ kind: "resend", m })}>
          {t("staff.resend.confirm")}
        </Button>
      )}
      {m.status === "ACTIVE" && (
        <Button size="sm" variant="ghost" onClick={() => onAction({ kind: "suspend", m })}>
          {t("staff.cmd.suspend.confirm")}
        </Button>
      )}
      {m.status === "SUSPENDED" && (
        <Button size="sm" variant="ghost" onClick={() => onAction({ kind: "resume", m })}>
          {t("staff.cmd.resume.confirm")}
        </Button>
      )}
      <Button size="sm" variant="ghost" onClick={() => onAction({ kind: "role", m })}>
        {t("staff.changeRole")}
      </Button>
      <Button size="sm" variant="danger" onClick={() => onAction({ kind: "remove", m })}>
        {t("staff.cmd.remove.confirm")}
      </Button>
    </div>
  );
}

function RoleDialog({
  m,
  roles,
  onClose,
  onDone,
}: {
  m: StaffMembershipItem;
  /** What the caller may grant on the row's node. */
  roles: StaffRole[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const [role, setRole] = useState<StaffRole>(m.role);
  return (
    <ConfirmReasonDialog
      title={t("staff.roleTitle", { user: memberLabel(m) })}
      description={t("staff.roleBody", { role: t(`role.${m.role}`) })}
      confirmLabel={t("staff.changeRole")}
      canSubmit={role !== m.role}
      onConfirm={async (reason) => {
        const res = await changeStaffRole(m.broker_id, m.membership_id, { role, reason });
        return { auditIds: res.auditIds };
      }}
      onClose={onClose}
      onDone={() => {
        onDone();
        onClose();
      }}
    >
      <Select
        label={t("staff.newRole")}
        value={role}
        onChange={(e) => setRole(e.target.value as StaffRole)}
        options={roles.map((r) => ({ value: r, label: t(`role.${r}`) }))}
        hint={t("staff.roleHint")}
      />
    </ConfirmReasonDialog>
  );
}

/** A member's name and email (SM-968); a row from before invites by email shows its raw id. */
function MemberName({ m }: { m: StaffMembershipItem }) {
  const name = m.display_name?.trim();
  if (!m.email) return <Id value={m.user_id} />;
  return (
    <span className="block min-w-0">
      <span className="block truncate text-sm font-semibold text-t-1">{name || m.email}</span>
      {name && <span className="block truncate text-2xs text-t-3">{m.email}</span>}
    </span>
  );
}
